import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getTag, getTags } from '@/hooks/useMagazines';

interface MagazineCardProps {
  magazine: NostrEvent;
  issueCount?: number;
}

export function MagazineCard({ magazine, issueCount }: MagazineCardProps) {
  const dTag = getTag(magazine, 'd');
  const title = getTag(magazine, 'title');
  const image = getTag(magazine, 'image');
  const summary = getTag(magazine, 'summary');
  const tags = getTags(magazine, 't');

  if (!dTag) return null;

  return (
    <Link to={`/magazine/${dTag}`}>
      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group h-full flex flex-col">
        <div className="relative overflow-hidden bg-muted aspect-[2/3] flex-shrink-0">
          {image ? (
            <img
              src={image}
              alt={title ?? 'Magazine'}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-primary/20 to-primary/5">
              <BookOpen className="h-12 w-12 text-primary/50" />
              <span className="text-sm text-muted-foreground text-center px-2 font-medium">{title}</span>
            </div>
          )}
          {issueCount !== undefined && issueCount > 0 && (
            <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full px-2 py-0.5">
              {issueCount} {issueCount === 1 ? 'Issue' : 'Issues'}
            </div>
          )}
        </div>

        <CardContent className="pt-3 pb-2 flex-1">
          <h3 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
            {title ?? 'Untitled'}
          </h3>
          {summary && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{summary}</p>
          )}
        </CardContent>

        {tags.length > 0 && (
          <CardFooter className="pt-0 pb-3 flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, i) => (
              <Badge key={i} variant="secondary" className="text-xs px-1.5 py-0">
                {tag}
              </Badge>
            ))}
          </CardFooter>
        )}
      </Card>
    </Link>
  );
}
